import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { t } from "ttag";

import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackPlanPageViewed } from "@/features/analytics/trackers/plan-page-viewed";
import { useOnboardingUser } from "@/features/onboarding/hooks";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { productsQueriesStore } from "@/features/products/api";
import { LogoLoader } from "@/shared/components";
import { BILLING_FREQUENCY, CATEGORY_SLUG } from "@/shared/domains";
import { Layout, SectionHeader } from "@/shared/ui";
import { LONGEVITY_ROUTE } from "../router/constants";
import { useLongevityStore } from "../stores";
import { LongevityCheckoutForm } from "./longevity-checkout-form";

export const LongevityCheckout = () => {
  const { analytics } = useCustomerIoContext();
  const user = useOnboardingUser();
  const { productSlug, frequency } = useLongevityStore();

  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const setSelectedCategories = useOnboardingStore(
    (state) => state.setSelectedCategories,
  );
  const addSelectedProduct = useOnboardingStore(
    (state) => state.addSelectedProduct,
  );
  const setSelectedPrice = useOnboardingStore(
    (state) => state.setSelectedPrice,
  );
  const couponCode = useOnboardingStore((state) => state.couponCode);

  const { data: product, isLoading } = useQuery({
    ...productsQueriesStore.products.category._ctx.products({
      categorySlug: CATEGORY_SLUG.LONGEVITY,
      slug: productSlug ?? undefined,
      frequency: frequency ?? undefined,
      couponCode,
    }),
    select: (productGroups) =>
      productGroups
        .flatMap((productGroup) => productGroup.products)
        .find((groupProduct) => groupProduct.slug === productSlug),
  });

  const price = useMemo(() => {
    if (!product || frequency === undefined) {
      return undefined;
    }
    return (
      product.prices.find(
        (productPrice) => productPrice.durationInMonths === frequency,
      ) ??
      product.prices.find(
        (productPrice) =>
          productPrice.durationInMonths === BILLING_FREQUENCY.SEMI_ANNUALLY,
      ) ??
      product.prices.at(-1)
    );
  }, [product, frequency]);

  const ready = selectedCategories.some(
    (category) => category.id === product?.category.id,
  );

  useEffect(() => {
    if (!product || !price) {
      return;
    }
    setSelectedCategories([{ ...product.category, isNew: false }]);
    addSelectedProduct(product);
    setSelectedPrice(product.category.id, price);
  }, [
    product,
    price,
    setSelectedCategories,
    addSelectedProduct,
    setSelectedPrice,
  ]);

  useEffect(() => {
    if (!user?.email) {
      return;
    }

    trackPlanPageViewed({
      customerIoAnalytics: analytics,
      user: user,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <Navigate to={LONGEVITY_ROUTE.ACCOUNT_PERSONAL_DETAILS} replace />;
  }

  if (isLoading || !ready) {
    return <LogoLoader show fullscreen>{t`Loading...`}</LogoLoader>;
  }

  if (!product || !price) {
    return <Navigate to={LONGEVITY_ROUTE.ACCOUNT_PERSONAL_DETAILS} replace />;
  }

  return (
    <Layout.Content>
      <SectionHeader
        title={t`Almost there! Pay for your order`}
        description={t`Fill out your details and payment method to complete your purchase.`}
      />

      <LongevityCheckoutForm />
    </Layout.Content>
  );
};
